import { useHistory, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { useState } from 'react'
import PropTypes from 'prop-types'
import ExportPlanModal from '../../../../components/export-plan-modal'
import {
  pinSizeOptions,
  pinSizeApiValues
} from '../../../../components/export-plan-modal/constants'
import { usePlanMenuListViewControl } from '../../../../hooks/use-plan-menu-list-view-control'
import { useExportPlanStatusTracker } from '../../../../hooks/use-export-plan-status-tracker'
import { useGetPlanDetailsQuery } from '../../../../react-query/plan/query/use-get-plan-details-query'
import { useGetPlanPinsCountQuery } from '../../../../react-query/pins/query/use-get-plan-pins-count-query'
import { useCreateFilterMutation } from '../../../../react-query/filters/mutation/use-create-filter-mutation'
import { useExportPlanMutation } from '../../../../react-query/plan/mutation/use-export-plan-mutation'
import { makeSelectProjectDetails } from '../../../ProjectSettings/selectors'
import { useSnackbar } from '../../../../components/stories/molecules/snackbar/component/snackbar-hook'
import { snackbarVariantOptions } from '../../../../components/stories/molecules/snackbar/constants'
import { buildPlanPinsFilter, buildExportFileName } from './utils'

// `filterId` is the pin filter currently applied to the plan being exported.
// There's no reliable way to look that up here: it's UI state owned by
// whichever plan viewer has the plan open (if any), so callers that know it
// pass it down as a prop. The plan-menu route usage doesn't have a plan
// open, so it simply omits it and exports without a filter.
const ExportSinglePlanModal = ({ filterId, onClose, onSuccess } = {}) => {
  const history = useHistory()
  const { projectId, planId } = useParams()
  const { formatMessage } = useIntl()
  const { viewType } = usePlanMenuListViewControl()

  const { addSnackbar } = useSnackbar()
  const projectDetails = useSelector(makeSelectProjectDetails())
  const { trackExportPlanJob } = useExportPlanStatusTracker()

  const [isIncludingPins, setIsIncludingPins] = useState(false)
  const [pinSize, setPinSize] = useState(pinSizeOptions.MEDIUM)

  const hasFilterApplied = !!filterId

  const defaultExportPlanSuccess = () =>
    history.push(`/project/${projectId}/importStatus?view=${viewType}`)

  const exportPlanFailed = () => {
    return addSnackbar({
      // The copy is pluralised on `value` (not `count`), and this modal always
      // exports a single plan.
      message: formatMessage({ id: 'plans_dialog_export_error' }, { value: 1 }),
      snackbarType: snackbarVariantOptions.ERROR,
      hideCloseButton: true
    })
  }

  // queries
  const { data: filteredPinsCount } = useGetPlanPinsCountQuery({
    planId,
    filterId,
    options: { enabled: !!planId && hasFilterApplied }
  })

  const { data: totalPinsCount } = useGetPlanPinsCountQuery({
    planId,
    options: { enabled: !!planId }
  })

  const { data: planDetails, isLoading: isLoadingPlanDetails } =
    useGetPlanDetailsQuery(planId)

  // mutations
  const { mutateAsync: createFilter, isPending: isCreatingFilter } =
    useCreateFilterMutation()

  const { mutate: exportPlan, isPending: isExporting } = useExportPlanMutation({
    onSuccess: (data, variables) => {
      trackExportPlanJob(data, variables.planName, variables.fileName)

      if (onSuccess) return onSuccess(data, variables)
      return defaultExportPlanSuccess()
    },
    onError: exportPlanFailed
  })

  const resolveFilterId = async () => {
    if (!isIncludingPins) return undefined
    if (filterId) return filterId

    const filter = await createFilter({
      filter: buildPlanPinsFilter({ projectId, planId })
    })
    return filter.id
  }

  // handlers
  const handleExportPlan = async () => {
    if (!revisionId || !planDetails || !projectDetails) return

    try {
      const resolvedFilterId = await resolveFilterId()

      exportPlan({
        revisionId,
        filterId: resolvedFilterId,
        includePins: isIncludingPins,
        pinSize: pinSizeApiValues[pinSize],
        planName: planDetails.name,
        fileName: buildExportFileName(projectDetails.name, planDetails.name)
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[ExportSinglePlanModal] handleExportPlan failed', error)
      exportPlanFailed()
    }
  }

  // helpers
  const revisionId = planDetails?.currentRevision?.id
  const selectedPinsCount = hasFilterApplied
    ? (filteredPinsCount ?? null)
    : null

  return (
    <ExportPlanModal
      title={formatMessage({ id: 'exportPlan_title' })}
      onExport={handleExportPlan}
      onClose={onClose}
      isLoading={isLoadingPlanDetails || isCreatingFilter || isExporting}
      hasFilterApplied={hasFilterApplied}
      totalPins={totalPinsCount}
      selectedPinsCount={selectedPinsCount}
      isIncludingPins={isIncludingPins}
      handleIsIncludingPins={setIsIncludingPins}
      pinSize={pinSize}
      handleChangePinSize={setPinSize}
    />
  )
}

ExportSinglePlanModal.propTypes = {
  filterId: PropTypes.string,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func
}

export default ExportSinglePlanModal
